import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, FileText, Copy, Star, Trash2, Pencil } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';

export default function Bids() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [bids, setBids] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const navigate = useNavigate();

  const load = () => {
    api.get('/bids', { params: { is_template: false } }).then(r => setBids(Array.isArray(r.data) ? r.data : r.data.data || [])).catch(() => {});
    api.get('/bids/templates').then(r => setTemplates(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const handleDuplicate = async (id) => {
    const r = await api.post(`/bids/${id}/duplicate`);
    toast.success('Bid duplicated');
    navigate(`/bids/${r.data.id}`);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/bids/${deleteTarget.id}`);
      toast.success('Bid deleted');
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const statusColor = (s) => {
    const map = { draft: 'bg-slate-100 text-slate-600', sent: 'bg-sky-100 text-sky-700',
      won: 'bg-emerald-100 text-emerald-700', lost: 'bg-red-100 text-red-700' };
    return map[s] || map.draft;
  };

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="bids-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">Bids</h1>
          <p className="text-slate-500 text-sm mt-1">{bids.length} bids</p>
        </div>
        <Button className="bg-sky-500 hover:bg-sky-600 text-white" onClick={() => navigate('/bids/new')} data-testid="new-bid-btn">
          <Plus className="w-4 h-4 mr-1" /> New Bid
        </Button>
      </div>

      {templates.length > 0 && (
        <div className="mb-6">
          <h2 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-1.5">
            <Star className="w-4 h-4 text-amber-500" /> Templates
          </h2>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {templates.map(t => (
              <Card key={t.id} className="min-w-[200px] border-amber-200 bg-amber-50/50 cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => handleDuplicate(t.id)} data-testid={`template-${t.id}`}>
                <CardContent className="p-4">
                  <p className="text-sm font-semibold text-slate-800">{t.template_name || 'Template'}</p>
                  <p className="text-xs text-slate-500 mt-1">${(t.totals?.total_price || 0).toLocaleString()}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-3">
        {bids.map((bid, i) => (
          <Card
            key={bid.id}
            className={`border-slate-100 hover:shadow-md transition-all cursor-pointer animate-fade-in stagger-${Math.min(i + 1, 5)}`}
            onClick={() => navigate(`/bids/${bid.id}`)}
            data-testid={`bid-card-${bid.id}`}
          >
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-4 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-sky-50 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-sky-500" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-800 truncate">
                    {bid.inputs?.customer_name || 'Untitled Bid'}
                  </p>
                  <p className="text-xs text-slate-400">
                    {bid.inputs?.city || ''} {bid.inputs?.pool_type ? `| ${bid.inputs.pool_type}` : ''} | {new Date(bid.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-lg font-bold font-mono text-slate-800">
                  ${(bid.totals?.total_price || 0).toLocaleString()}
                </span>
                <Badge className={statusColor(bid.status)}>{bid.status}</Badge>
                {bid.frozen && <Badge className="bg-amber-50 text-amber-700 text-[9px]">Locked</Badge>}
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); navigate(`/bids/${bid.id}`); }}
                  data-testid={`edit-bid-${bid.id}`}>
                  <Pencil className="w-4 h-4 text-amber-500" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); handleDuplicate(bid.id); }}
                  data-testid={`duplicate-bid-${bid.id}`}>
                  <Copy className="w-4 h-4 text-slate-400" />
                </Button>
                {isAdmin && (
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); setDeleteTarget({ id: bid.id, name: bid.inputs?.customer_name || 'Untitled' }); }}
                    data-testid={`delete-bid-${bid.id}`}>
                    <Trash2 className="w-4 h-4 text-red-400 hover:text-red-600" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
        {bids.length === 0 && (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No bids yet. Create your first bid!</p>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="delete-bid-dialog">
          <DialogHeader>
            <DialogTitle>Delete Bid</DialogTitle>
          </DialogHeader>
          <p className="text-sm font-medium text-[#4a6a7e]">
            Are you sure you want to delete the bid for <span className="font-bold text-[#1e3a50]">{deleteTarget?.name}</span>? This cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button className="bg-red-500 hover:bg-red-600 text-white" onClick={handleDelete} data-testid="confirm-delete-bid-btn">
              Delete Bid
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
